/**
 * @file theme-dom.js
 * @description Shared DOM helpers for every theme script: the 820px mobile edge
 * (isDesktopViewport, which reads the stylesheets' own (width > 820px) media
 * query, and onViewportEdgeChange), ready-state boot, debounce, scoped
 * queries, focus helpers (getFocusableElements, safelyFocus, cycleFocusWithin),
 * plain-click detection, hidden/aria-hidden pairing, whitespace normalisation
 * and the store-root URL helper for Cart API calls. Loaded by
 * layout/theme.liquid directly after theme-formatters.js on every template, so
 * consumers read window.ThemeDom without a fallback.
 */
(function () {
  'use strict';

  const MOBILE_EDGE = 820;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /**
   * The desktop range exactly as the stylesheets write it (D-012). Under
   * browser zoom the CSS viewport width can be fractional while innerWidth is
   * a whole number, so near the edge the two could disagree; one media query
   * list, created once and read live, keeps the script on the CSS's side.
   * An engine without range syntax never matches it, here as in the CSS.
   */
  const desktopQuery = window.matchMedia('(width > ' + MOBILE_EDGE + 'px)');

  function isDesktopViewport() {
    return desktopQuery.matches;
  }

  /**
   * Call `callback(isDesktop)` after a debounced resize, but only when the
   * viewport crossed the mobile edge since the previous call.
   */
  function onViewportEdgeChange(callback, waitMs = 200) {
    let lastIsDesktop = isDesktopViewport();

    window.addEventListener('resize', debounce(() => {
      const isDesktop = isDesktopViewport();

      if (isDesktop === lastIsDesktop) {
        return;
      }

      lastIsDesktop = isDesktop;
      callback(isDesktop);
    }, waitMs));
  }

  function onReady(callback) {
    if (typeof callback !== 'function') {
      return;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function debounce(callback, wait) {
    let timer = null;

    return function debounced(...args) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        callback.apply(this, args);
      }, wait);
    };
  }

  /**
   * Elements matching `selector` inside `root`, including `root` itself when it
   * matches. Anything that cannot be queried (null, a text node) falls back to
   * the document.
   */
  function queryWithin(root, selector) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    const matches = [];

    if (scope instanceof Element && scope.matches(selector)) {
      matches.push(scope);
    }

    scope.querySelectorAll(selector).forEach((element) => {
      matches.push(element);
    });

    return matches;
  }

  function isFocusableElement(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    if (element.hidden || element.matches('[disabled]') || element.closest('[hidden]')) {
      return false;
    }

    return element.getClientRects().length > 0;
  }

  function getFocusableElements(container) {
    if (!container) {
      return [];
    }

    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusableElement);
  }

  function safelyFocus(element) {
    if (!(element instanceof HTMLElement) || typeof element.focus !== 'function') {
      return;
    }

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  }

  /**
   * Move focus to the next (or previous) focusable element inside `container`,
   * wrapping at either end; from outside the list (the container itself, or
   * nothing inside it) Tab lands on the first element and Shift+Tab on the
   * last. A container with nothing focusable receives focus itself. Callers
   * preventDefault() the Tab keydown themselves.
   */
  function cycleFocusWithin(container, backwards) {
    const focusableElements = getFocusableElements(container);

    if (!focusableElements.length) {
      safelyFocus(container);
      return;
    }

    const lastIndex = focusableElements.length - 1;
    const currentIndex = focusableElements.indexOf(document.activeElement);
    let nextIndex;

    if (currentIndex === -1) {
      nextIndex = backwards ? lastIndex : 0;
    } else if (backwards) {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    } else {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    }

    safelyFocus(focusableElements[nextIndex]);
  }

  /**
   * A primary-button click with no modifier key that nothing has already
   * handled: the only kind a script may turn into in-page navigation.
   */
  function isPlainPrimaryClick(event) {
    if (!event || event.defaultPrevented) {
      return false;
    }

    if (typeof event.button === 'number' && event.button !== 0) {
      return false;
    }

    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  }

  function setElementHidden(element, hidden) {
    if (!(element instanceof Element)) {
      return;
    }

    element.hidden = Boolean(hidden);

    if (hidden) {
      element.setAttribute('aria-hidden', 'true');
    } else {
      element.removeAttribute('aria-hidden');
    }
  }

  function normalizeWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Absolute path under the store root (window.Shopify.routes.root, '/' when
   * unset), so Cart API requests keep a locale or market prefix. `path` is
   * given without a leading slash, e.g. storeUrl('cart/add.js').
   */
  function storeUrl(path) {
    const routes = window.Shopify && window.Shopify.routes;
    const root = routes && typeof routes.root === 'string' && routes.root ? routes.root : '/';

    return root.replace(/\/?$/, '/') + String(path || '').replace(/^\/+/, '');
  }

  window.ThemeDom = {
    isDesktopViewport,
    onViewportEdgeChange,
    onReady,
    debounce,
    queryWithin,
    isFocusableElement,
    getFocusableElements,
    safelyFocus,
    cycleFocusWithin,
    isPlainPrimaryClick,
    setElementHidden,
    normalizeWhitespace,
    storeUrl
  };
})();
